'use client';

import { IconDefinition, faTimes } from '@fortawesome/pro-light-svg-icons';
import { VariantProps, tv } from 'tailwind-variants';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { Slot } from '@radix-ui/react-slot';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { Icon } from '@/components/ui/icon.component';

const pillStyle = tv({
  slots: {
    container: ['px-2 py-1 rounded-md border w-fit flex items-center gap-1 text-base', ' font-semibold'],
    description: ['text-xs font-normal mt-1'],
    closeIcon: ['mt-[3px]'],
    startIcon: ['mt-[1px]'],
  },

  variants: {
    variant: {
      success: {
        container: ['bg-success-100 text-success-700 border-success-400'],
        icon: ['text-success-400'],
        description: ['text-success-600'],
      },
      warning: {
        container: ['bg-warning-100 text-warning-700 border-warning-400'],
        icon: ['text-warning-500'],
      },
      danger: {
        container: ['bg-danger-100 text-danger-700 border-danger-400'],
        icon: ['text-danger-600'],
      },
      info: {
        container: ['bg-blue-100 text-blue-600 border-blue-600 hover:bg-blue-200'],
        icon: ['text-blue-600'],
      },
      light: {
        container: ['bg-gray-100 text-gray-700 border-gray-400'],
        icon: ['text-gray-400'],
      },
      search: {
        container: ['bg-pink-100 border-pink-100 text-pink-700'],
        icon: ['text-pink-700'],
      },
    },
    hasAction: {
      true: {},
    },
    hasDescription: {
      true: {},
    },
  },

  compoundVariants: [
    {
      variant: 'success',
      hasAction: true,
      className: {
        container: ['hover:bg-success-100 hover:border-success-300 hover:text-success-700', 'font-normal'],
      },
    },
    {
      variant: 'danger',
      hasAction: true,
      className: {
        container: ['hover:bg-danger-100 hover:border-danger-300 hover:text-danger-700 font-normal'],
      },
    },
    {
      hasDescription: true,
      className: {
        container: 'p-3 gap-3',
      },
    },
  ],

  defaultVariants: {
    variant: 'success',
    hasAction: false,
  },
});

export type VariantsPillProps = VariantProps<typeof pillStyle>;

export interface PillProps extends Omit<VariantsPillProps, 'hasDescription'>, React.HTMLAttributes<HTMLElement> {
  asChild?: boolean;
  closable?: boolean;
  icon?: IconDefinition;
  label?: string;
  description?: string;
  className?: string;
  iconSize?: SizeProp;
  children?: React.ReactNode;
  onClose?: () => void;
  action?: (e: React.MouseEvent | React.TouchEvent) => void;
}

const elementMap = {
  div: 'div',
  button: 'button',
} as const;

function getElementName(hasAction: boolean, closable: boolean): keyof typeof elementMap {
  switch (hasAction) {
    case true:
      return !closable ? 'button' : 'div';
    default:
      return 'div';
  }
}

export function Pill({
  label,
  description,
  hasAction,
  closable,
  variant,
  icon: startIcon,
  className,
  iconSize: startIconSize,
  asChild = false,
  children,
  onClose,
  action,
  ...rest
}: PillProps) {
  const hasDescription = Boolean(description);
  const dataTestId = rest['data-testid'];

  const {
    container,
    closeIcon,
    description: descriptionStyle,
    startIcon: startIconStyle,
  } = pillStyle({ variant, hasAction, hasDescription });

  if ((asChild && !children) || (!asChild && children)) {
    throw new Error('Pill component must have children and asChild prop must be true');
  }

  const elementName = getElementName(Boolean(hasAction), Boolean(closable));
  const Element = asChild ? Slot : elementMap[elementName];

  const iconSize = hasDescription ? 'lg' : 'sm';

  if (asChild) {
    return (
      <Element className={container({ className })} {...rest} data-testid={dataTestId ?? DataTestIdEnum.PILL_CONTAINER}>
        {children}
      </Element>
    );
  }

  return (
    <Element
      className={container({ className })}
      onClick={(e) => {
        action?.(e);
      }}
      onTouchEnd={(e) => {
        action?.(e);
      }}
      data-testid={dataTestId ?? DataTestIdEnum.PILL_CONTAINER}
      {...rest}
    >
      {startIcon && (
        <Icon
          data-testid={DataTestIdEnum.PILL_START_ICON}
          icon={startIcon}
          size={startIconSize || 'sm'}
          className={startIconStyle()}
        />
      )}

      <div className="flex justify-content truncate">
        <span data-testid={DataTestIdEnum.PILL_LABEL} className="truncate">
          {label}
        </span>
        {hasDescription && (
          <p className={descriptionStyle()} data-testid={DataTestIdEnum.PILL_DESCRIPTION}>
            {description}
          </p>
        )}
      </div>

      {closable && (
        <button onClick={onClose} data-testid={DataTestIdEnum.PILL_CLOSE}>
          <Icon icon={faTimes} size={iconSize} className={closeIcon()} />
        </button>
      )}
    </Element>
  );
}
